import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { GroupByOption, ITileComponent, StoredMetric } from '../common-tile.model';
import { CalcEngineConstants, CalcEngineRequest, CalcEngineResponse, Metric } from 'src/app/services/models/calc-engine.model';
import { UserBoardComponentMode } from 'src/app/services/models/user-board.model';
import { SelectedFilterUpdatesService } from 'src/app/common/bhub-common-module/services/selected-filter-updates.service';
import { CalcEngineService } from 'src/app/services/calc-engine.service';
import { MetricService } from 'src/app/services/metric.service';
import { DatasetService } from 'src/app/services/dataset.service';
import { Dataset, DatasetType } from 'src/app/services/models/datasets.models';
import { PieMetric, StoredPieTileDefinition, PieChartDisplayMode } from './pie-tile.model';

@Component({
  selector: 'bhub-pie-tile',
  standalone: false,
  templateUrl: './pie-tile.component.html',
  styleUrl: './pie-tile.component.scss'
})
export class PieTileComponent implements OnInit, ITileComponent {
  @Input() data: StoredPieTileDefinition;
  @Input() mode: UserBoardComponentMode;

  @Output() onPreviewItemClickEvent = new EventEmitter<string>();

  UserBoardComponentMode = UserBoardComponentMode;
  PieChartDisplayMode = PieChartDisplayMode;
  availableMetrics: StoredMetric[];
  filtersApplied: any = {};

  model: StoredPieTileDefinition = new StoredPieTileDefinition("", true, null, [], PieChartDisplayMode.SinglePie);

  public initialized = false;
  public chartData: any = {};
  public chartOptions: any = {};
  public datasets: Dataset[] = [];
  public selectedDataset: Dataset | null = null;
  public availableFields: {fieldId: number, fieldName: string}[] = [];

  selectedMetric: StoredMetric | null = null;
  selectedColor: string = '#3f51b5';
  selectedGroupName: string = '';
  availableGroups: string[] = [];

  private mockLabels = ["Category1", "Category2", "Category3", "Category4", "Category5"];
  private mockMetricValues = {
    'ActualRevenue': 45000,
    'ContractRevenue': 32000,
    'OpportunityRevenue': 18000
  };

  constructor(
    private calcEngineService: CalcEngineService,
    private metricService: MetricService,
    private selectedFilterUpdateObserver: SelectedFilterUpdatesService,
    private datasetService: DatasetService,
  ) {
  }

  public validate(): string{
    if(this.model.useGroupBy) {
      if(!this.model.groupBy || this.model.groupBy.datasetId == ''){
        return 'Please select a dataset for grouping';
      } else if(this.model.groupBy.datasetPrimaryKey == '' || this.model.groupBy.fieldToDisplay == '' || this.model.groupBy.fieldIdToDisplay == ''){
        return 'Please select a field to group by';
      }
      
      // GroupBy mode constraints
      if(this.model.metrics.length > 1){
        return 'When using Group By, only one metric is allowed';
      }
    }
    
    if(this.model.metrics.length == 0){
      return 'Please add at least one metric to the pie chart';
    }
    
    return '';
  }

  public ngOnInit() {
    this.selectedFilterUpdateObserver.filters$.subscribe((filters) => {
      this.filtersApplied = filters;
      this.makeServiceRequest();
    });

    if(this.mode === UserBoardComponentMode.Edit){
      this.datasetService.getDatasetReferences().subscribe((datasets: Map<number, Dataset>) => {
        this.datasets = Array.from(datasets.values()).filter((dataset) => dataset.datasetReferenceTable.datasetType === DatasetType.Reference || dataset.datasetReferenceTable.datasetType === DatasetType.View);
      });
    }
  }

  onUseGroupByToggle(useGroupBy: boolean): void {
    this.model.useGroupBy = useGroupBy;
    
    // Always ensure Single Pie Chart mode (only supported mode)
    this.model.displayMode = PieChartDisplayMode.SinglePie;
    
    if (!useGroupBy) {
      // Switching to metrics-only mode
      this.model.groupBy = null;
      this.selectedDataset = null;
      this.availableFields = [];
    } else {
      // Switching to GroupBy mode - enforce constraints
      if (!this.model.groupBy) {
        this.model.groupBy = new GroupByOption("", "", "", "");
      }
      
      // Keep only the first metric if multiple exist
      if (this.model.metrics.length > 1) {
        this.model.metrics = [this.model.metrics[0]];
        
        // Rebuild chart data with single metric
        this.buildMockGroupByData(this.model);
        this.chartData = {
          ...this.chartData,
          datasets: [...this.chartData.datasets]
        };
      }
    }
  }

  onDatasetChange(datasetId: number | string): void {
    this.selectedDataset = this.datasets.find(d => d.datasetReferenceTable.datasetId === Number(datasetId));
    this.availableFields = this.selectedDataset ? this.selectedDataset.dataTableSchemas.map(schema => ({fieldId: schema.fieldId, fieldName: schema.displayName})) : [];

    if (!this.model.groupBy) {
      this.model.groupBy = new GroupByOption("", "", "", "");
    }
    this.model.groupBy.datasetPrimaryKey = '';
    this.model.groupBy.fieldToDisplay = '';
    this.model.groupBy.fieldIdToDisplay = '';
  }

  onFieldChange(fieldName: string): void {
    if (!this.selectedDataset) return;
    
    if (!this.model.groupBy) {
      this.model.groupBy = new GroupByOption("", "", "", "");
    }
    
    const primaryKeyFields = this.selectedDataset.dataTableSchemas.filter(schema => schema.isPrimaryKey);
    if (primaryKeyFields.length === 0) return;
    
    const primaryKeyFieldId = primaryKeyFields[0].fieldId.toString();
    
    const selectedField = this.availableFields.find(f => f.fieldName === fieldName);
    if (selectedField) {
      const fieldIdToDisplay = selectedField.fieldId.toString();
      
      this.model.groupBy.datasetPrimaryKey = primaryKeyFieldId;
      this.model.groupBy.fieldIdToDisplay = fieldIdToDisplay;
      this.model.groupBy.fieldToDisplay = fieldName;
    } else if (fieldName === CalcEngineConstants.PRIMARY_KEY) {
      this.model.groupBy.datasetPrimaryKey = CalcEngineConstants.PRIMARY_KEY;
      this.model.groupBy.fieldIdToDisplay = CalcEngineConstants.PRIMARY_KEY;
      this.model.groupBy.fieldToDisplay = fieldName;
    }
  }

  showPrimaryKey(): boolean{
    if(this.selectedDataset == null) return false;
    return this.selectedDataset.dataTableSchemas.filter(schema => schema.isPrimaryKey).map(schema=>schema.displayName).length > 1;
  }

  getPrimaryKeyValue(){
    return CalcEngineConstants.PRIMARY_KEY;
  }

  makeServiceRequest(): void{
    this.initialized = false;

    if(this.mode === UserBoardComponentMode.Live){
      var request = this.generateTileRequest(this.data);
      this.calcEngineService.runCalcEngine(request, false).subscribe( (response) => {
        this.buildModelFromResponse(this.data, response);
        this.initialized = true;
      });
    }
    else if( this.mode === UserBoardComponentMode.Preview) {
      this.buildModelFromMockResponse(this.data);
      this.initialized = true;
    }
    else if( this.mode === UserBoardComponentMode.Edit) {
      if(this.data != null){
        this.buildInputModel(this.data);
        this.initialized = true;
      } else{
        this.buildInputModel();
        this.initialized = true;
      }
    }
  }

  generateTileRequest(data: StoredPieTileDefinition): CalcEngineRequest {
    const request = new CalcEngineRequest();

    if(data.useGroupBy && data.groupBy) {
      request.groupBy.push(data.groupBy.datasetPrimaryKey.toString());
      
      if (data.groupBy.fieldIdToDisplay !== data.groupBy.datasetPrimaryKey) {
        request.additionalFields[data.groupBy.datasetId] = [data.groupBy.fieldIdToDisplay];
      }
    }
    // For no group by mode, we don't add any groupBy - this will return aggregate metrics
    
    request.metricList = data.metrics.map((pieMetric) => pieMetric.metric as Metric);
    request.filters = this.filtersApplied;

    return request;
  }

  buildModelFromResponse(data: StoredPieTileDefinition, response: CalcEngineResponse): void {
    if(data.useGroupBy && data.groupBy) {
      // Group by mode - data is grouped by dataset field
      const fieldIdToDisplay = data.groupBy.fieldIdToDisplay.toString();
      const labels = response.rowData.map(row => row[fieldIdToDisplay] || 'Unknown');
      
      this.buildGroupByChartData(data, response, labels);
    } else {
      // No group by mode - show metrics as individual slices
      this.buildMetricsOnlyChartData(data, response);
    }
    
    this.model = data;
    this.chartOptions = this.buildChartOptions();
  }

  private buildGroupByChartData(data: StoredPieTileDefinition, response: CalcEngineResponse, labels: string[]): void {
    // In GroupBy mode, we always show single pie (per our constraints)
    // With one metric showing distribution across groups
    if(data.metrics.length === 1) {
      const metric = data.metrics[0];
      const values = response.rowData.map((row) => row[metric.metric.metricName]);
      
      this.chartData = {
        labels: labels,
        datasets: [{
          data: values,
          backgroundColor: this.generateColorPalette(labels.length),
          hoverBackgroundColor: this.generateColorPalette(labels.length, 0.8)
        }]
      };
    } else {
      // This shouldn't happen due to constraints, but fallback
      const datasets = data.metrics.map((metric) => {
        return {
          label: metric.metric.metricName,
          data: response.rowData.map((row) => row[metric.metric.metricName]),
          backgroundColor: metric.color
        };
      });
      
      this.chartData = {
        labels: labels,
        datasets: datasets
      };
    }
  }

  private buildMetricsOnlyChartData(data: StoredPieTileDefinition, response: CalcEngineResponse): void {
    // In no group by mode, we show metrics as slices of the pie
    const rowData = response.rowData.length > 0 ? response.rowData[0] : {};
    
    // For now, only support Single Pie Chart mode
    const labels = data.metrics.map(m => m.metric.metricName);
    const values = data.metrics.map(m => rowData[m.metric.metricName] || 0);
    const colors = data.metrics.map(m => m.color || this.generateRandomColor());
    
    this.chartData = {
      labels: labels,
      datasets: [{
        data: values,
        backgroundColor: colors,
        hoverBackgroundColor: colors.map(color => this.adjustColorOpacity(color, 0.8))
      }]
    };
  }

  buildModelFromMockResponse(data: StoredPieTileDefinition): void {
    if(data.useGroupBy) {
      // Group by mode - show data grouped by mock categories
      this.buildMockGroupByData(data);
    } else {
      // No group by mode - show metrics as slices using mock values
      this.buildMockMetricsOnlyData(data);
    }
    
    this.model = data;
    this.chartOptions = this.buildChartOptions();
  }

  private buildMockGroupByData(data: StoredPieTileDefinition): void {
    const mockData = this.mockLabels.map(() => Math.random() * 100);
    
    // In GroupBy mode, we always show single pie (per our constraints)
    // With one metric showing distribution across groups
    if(data.metrics.length === 1) {
      this.chartData = {
        labels: this.mockLabels,
        datasets: [{
          data: mockData,
          backgroundColor: this.generateColorPalette(this.mockLabels.length),
          hoverBackgroundColor: this.generateColorPalette(this.mockLabels.length, 0.8)
        }]
      };
    } else {
      // This shouldn't happen due to constraints, but fallback
      const datasets = data.metrics.map((metric) => {
        return {
          label: metric.metric.metricName,
          data: this.mockLabels.map(() => Math.random() * 100),
          backgroundColor: metric.color
        };
      });
      
      this.chartData = {
        labels: this.mockLabels,
        datasets: datasets
      };
    }
  }

  private buildMockMetricsOnlyData(data: StoredPieTileDefinition): void {
    // For now, only support Single Pie Chart mode
    const labels = data.metrics.map(m => m.metric.metricName);
    const values = data.metrics.map(m => this.mockMetricValues[m.metric.metricName] || Math.random() * 50000);
    const colors = data.metrics.map(m => m.color || this.generateRandomColor());
    
    this.chartData = {
      labels: labels,
      datasets: [{
        data: values,
        backgroundColor: colors,
        hoverBackgroundColor: colors.map(color => this.adjustColorOpacity(color, 0.8))
      }]
    };
  }

  buildInputModel(data: StoredPieTileDefinition = null): void {
    this.metricService.getMetricList().subscribe((metrics) => {
      this.availableMetrics = [];
      metrics.forEach((metric) => {
        this.availableMetrics.push(new StoredMetric(metric.metricId, metric.metricName));
      });
      
      if(this.availableMetrics.length > 0) {
        this.selectedMetric = this.availableMetrics[0];
      }

      if(data == null){
        this.model.metrics = [];
        this.model.useGroupBy = true;
        this.model.groupBy = new GroupByOption("", "", "", "");
        this.model.displayMode = PieChartDisplayMode.SinglePie;
        this.buildMockMetricsOnlyData(this.model);
        this.chartOptions = this.buildChartOptions();
      }
      else {
        // Ensure groupBy is initialized
        if (!data.groupBy) {
          data.groupBy = new GroupByOption("", "", "", "");
        }
        
        // Set selected dataset if it exists
        if (data.groupBy.datasetId) {
          this.selectedDataset = this.datasets.find(d => d.datasetReferenceTable.datasetId === Number(data.groupBy.datasetId));
          this.availableFields = this.selectedDataset ? this.selectedDataset.dataTableSchemas.map(schema => ({fieldId: schema.fieldId, fieldName: schema.displayName})) : [];
        }
        
        // Use the proper chart building methods based on the data configuration
        this.model = data;
        
        if(data.useGroupBy) {
          this.buildMockGroupByData(data);
        } else {
          this.buildMockMetricsOnlyData(data);
        }
        
        this.chartOptions = this.buildChartOptions();
      }
    });
  }

  buildChartOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: 'black'
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              let label = context.label || '';
              if (label) {
                label += ': ';
              }
              const value = context.parsed;
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(1);
              label += value + ' (' + percentage + '%)';
              return label;
            }
          }
        }
      }
    };
  }

  generateColorPalette(count: number, alpha: number = 1): string[] {
    const colors = [
      `rgba(255, 99, 132, ${alpha})`,
      `rgba(54, 162, 235, ${alpha})`,
      `rgba(255, 205, 86, ${alpha})`,
      `rgba(75, 192, 192, ${alpha})`,
      `rgba(153, 102, 255, ${alpha})`,
      `rgba(255, 159, 64, ${alpha})`,
      `rgba(199, 199, 199, ${alpha})`,
      `rgba(83, 102, 255, ${alpha})`,
      `rgba(255, 99, 255, ${alpha})`,
      `rgba(99, 255, 132, ${alpha})`
    ];
    
    const result = [];
    for(let i = 0; i < count; i++) {
      result.push(colors[i % colors.length]);
    }
    return result;
  }

  private generateRandomColor(): string {
    const colors = [
      '#FF6384', '#36A2EB', '#FFCD56', '#4BC0C0', '#9966FF', 
      '#FF9F40', '#C7C7C7', '#5382FF', '#FF63FF', '#63FF84'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  private adjustColorOpacity(color: string, opacity: number): string {
    if (color.startsWith('#')) {
      // Convert hex to rgba
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    if (color.startsWith('rgba')) {
      // Replace existing alpha value
      return color.replace(/,\s*[\d.]+\)$/, `, ${opacity})`);
    }
    if (color.startsWith('rgb')) {
      // Convert rgb to rgba
      return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
    }
    return color;
  }

  onMetricAdd(metric: StoredMetric, color: string, groupName?: string): void {
    // Always ensure Single Pie Chart mode (only supported mode)
    this.model.displayMode = PieChartDisplayMode.SinglePie;
    
    // In GroupBy mode, replace the existing metric instead of adding
    if (this.model.useGroupBy && this.model.metrics.length > 0) {
      this.model.metrics = [new PieMetric(color, metric, groupName)];
    } else {
      this.model.metrics.push(new PieMetric(color, metric, groupName));
    }
    
    // Update available groups
    if (groupName && !this.availableGroups.includes(groupName)) {
      this.availableGroups.push(groupName);
    }
    
    // Rebuild chart data based on current mode
    if(this.model.useGroupBy) {
      this.buildMockGroupByData(this.model);
    } else {
      this.buildMockMetricsOnlyData(this.model);
    }
    
    this.chartData = {
      ...this.chartData,
      datasets: [...this.chartData.datasets]
    };
  }

  onMetricRemove(index: number): void {
    const removedMetric = this.model.metrics[index];
    this.model.metrics.splice(index, 1);
    
    // Update available groups if this was the last metric in a group
    if (removedMetric?.groupName) {
      const groupStillUsed = this.model.metrics.some(m => m.groupName === removedMetric.groupName);
      if (!groupStillUsed) {
        this.availableGroups = this.availableGroups.filter(g => g !== removedMetric.groupName);
      }
    }
    
    // Rebuild chart data
    if(this.model.useGroupBy) {
      this.buildMockGroupByData(this.model);
    } else {
      this.buildMockMetricsOnlyData(this.model);
    }
    
    this.chartData = {
      ...this.chartData,
      datasets: [...this.chartData.datasets]
    };
  }

  onDisplayModeChange(mode: PieChartDisplayMode): void {
    // For now, only support Single Pie Chart mode
    this.model.displayMode = PieChartDisplayMode.SinglePie;
    
    // Rebuild chart data
    if(this.model.useGroupBy) {
      this.buildMockGroupByData(this.model);
    } else {
      this.buildMockMetricsOnlyData(this.model);
    }
    
    this.chartData = {
      ...this.chartData,
      datasets: [...this.chartData.datasets]
    };
  }

  onPreviewButtonClick(iconText: string) {
    this.onPreviewItemClickEvent.emit(iconText);
  }
}